import { inject, injectable } from 'inversify';

import { CORE_TYPES } from '@/engine/app/modules/core';
import { TYPES } from '@/engine/ioc';

import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm.ts';
import type { ViewsService } from '@/engine/app/Views.service.ts';
import type {
  IBaseViewModel,
  IMeasurementsBoxesController,
  IMeasurementViewModel,
  IObjectViewModel,
  IViewItemsViewModelController
} from '@/engine/app/interfaces.ts';
import type { MeasurementProps } from '../types.ts';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import type { MeasurementBoxInfo } from '@/engine/app/models/viewEntity/view.entity.ts';

import { SceneItemAction } from '@/engine/app/interfaces.ts';
import type { ICommandProcessor } from '@/engine/app/modules/core/interfaces/Command.ts';
import type { ValuesCollection } from '@/engine/DataFlow/DataFlow.ts';
import type { IBoxViewModelModifier } from '@/components/boxes/common/interfaces.ts';
import type { WithUndefined, WithMixed } from '@/components/viewOption/types.ts';
import { AutoDisposer } from '@/engine/Utilities/AutoDisposer.ts';

export interface MeasurementBoxProps {
  kind: string;
  label: string;
  order: number;
  measurements: Array<MeasurementProps>;
  hasCriteria: boolean | undefined;
  group?: string;
}

@injectable()
export class MeasurementBoxesController implements IMeasurementsBoxesController {
  private readonly _viewsService: ViewsService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _viVmController: IViewItemsViewModelController;

  private readonly _vmModifier: IBoxViewModelModifier;

  private readonly _loggerService: ObservabilityLoggerService;

  private readonly _commandProcessor: ICommandProcessor;

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.IViewItemsViewModelController)
    viVmController: IViewItemsViewModelController,
    @inject(TYPES.IBoxViewModelModifier) vmModifier: IBoxViewModelModifier,
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm,
    @inject(CORE_TYPES.ObservabilityLoggerService) loggerService: ObservabilityLoggerService,
    @inject(CORE_TYPES.CommandProcessor) commandProcessor: ICommandProcessor
  ) {
    this._viewsService = viewsService;
    this._vmModifier = vmModifier;
    this._planningAlgorithm = planningAlgorithm;
    this._viVmController = viVmController;
    this._loggerService = loggerService;
    this._commandProcessor = commandProcessor;
  }

  // @computed
  public getMeasurementBoxes(): Array<MeasurementBoxProps> {
    const boxesDescriptors = this._viewsService.currentView?.getMeasurementBoxes();

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return boxesDescriptors!.map((measurementBox: MeasurementBoxInfo) => {
      const measurements = measurementBox.measurements;

      const measurementsProps = measurements.map((measurement: IMeasurementViewModel): MeasurementProps => {
        return {
          descriptor: measurement.descriptor,
          attachments: measurement.attachments,
          label: measurement.label,
          beingEdited: this._viVmController.isViewModelBeingEdited(measurement.descriptor, true),
          visible: measurement.isVisible,
          imported: measurement.imported,
          syncSliceDescriptor: measurement.syncSliceDescriptor,
          details: measurement.details,
          kind: measurement.kind,
          color: measurement.color,
          isLabelColored: measurement.isLabelColored
        };
      });

      return {
        kind: measurementBox.kind,
        group: measurementBox.group,
        label: measurementBox.label,
        measurements: measurementsProps,
        order: measurementBox.order,
        hasCriteria: measurementBox.hasCriteria
      };
    });
  }

  public checkGroupViewItemsVisibility(descriptors: Array<string>): WithUndefined<WithMixed<boolean>> {
    if (descriptors.length === 0) {
      return undefined;
    }

    const allVisible = descriptors.every((d) => this._findViewItem(d)?.isVisible === true);
    const allHidden = descriptors.every((d) => this._findViewItem(d)?.isVisible === false);

    if (allVisible) {
      return true;
    }
    if (allHidden) {
      return false;
    }
    return 'mixed';
  }

  public checkGroupViewItemsBeingEdited(descriptors: Array<string>): WithUndefined<WithMixed<boolean>> {
    if (descriptors.length === 0) {
      return undefined;
    }

    const allBeingEdited = descriptors.every((d) => this._viVmController.isViewModelBeingEdited(d, true) !== false);
    const allNotBeingEdited = descriptors.every((d) => this._viVmController.isViewModelBeingEdited(d, true) !== true);

    if (allBeingEdited) {
      return true;
    }
    if (allNotBeingEdited) {
      return false;
    }
    return 'mixed';
  }

  public updateAllViewItemsVisibility(descriptors: Array<string>, isVisible: boolean): void {
    descriptors.forEach((descriptor) => {
      this._findViewItem(descriptor)?.setVisibility(isVisible);
    });
  }

  public updateAllViewItemsBeingEdited(descriptors: Array<string>, isBeingEdited: boolean): void {
    descriptors.forEach((descriptor) => {
      this.setMeasurementBeingEdited(descriptor, isBeingEdited);
    });
  }

  private _findObjects(descriptor: string): IObjectViewModel | undefined {
    return this._viewsService.currentView?.getObjects().find((meas) => meas.descriptor == descriptor);
  }

  private _findMeasurement(descriptor: string): IMeasurementViewModel | undefined {
    return this._viewsService.currentView?.getMeasurements().find((meas) => meas.descriptor == descriptor);
  }

  private _findViewItem(descriptor: string): IBaseViewModel | undefined {
    return this._viewsService.currentView?.getViewItems().find((item) => item.descriptor == descriptor);
  }

  public setObjectHighlighting(descriptor: string, highlighted: boolean): void {
    this._findObjects(descriptor)?.setHighlighting(highlighted);
  }

  public setMeasurementVisibility(descriptor: string, visible: boolean): void {
    this._vmModifier.setViewModelVisibility(descriptor, visible);

    const vm = this._findMeasurement(descriptor);

    if (vm) {
      this._loggerService.dispatchSceneItemAction(SceneItemAction.IndicationVisibilityChange, vm);
    }
  }

  public setMeasurementBeingEdited(descriptor: string, isBeingEdited: boolean): void {
    this._viVmController.setViewModelBeingEdited(descriptor, isBeingEdited, true);
    const identifier = `isBeingEdited$${descriptor}`;
    this._viewsService.getCurrentViewLifetime().remove(identifier);
    if (isBeingEdited) {
      this._viewsService
        .getCurrentViewLifetime()
        .set(identifier, new AutoDisposer(() => this._viVmController.setViewModelBeingEdited(descriptor, false, true)));
    }
  }

  public setMeasurementHighlighting(descriptor: string, highlighted: boolean): void {
    this._findMeasurement(descriptor)?.setHighlighting(highlighted);
  }

  public setMeasurementSelection(descriptor: string, isSelected: boolean): void {
    this._vmModifier.setViewModelSelection(descriptor, isSelected);
  }

  public async isDefaultAsync(
    descriptor: string,
    approvedMeansDefault?: boolean,
    attachmentNumber?: number
  ): Promise<boolean> {
    const measurement: IMeasurementViewModel | undefined = this._findMeasurement(descriptor);
    return (
      !measurement ||
      (await this._planningAlgorithm.areTuneValuesDefaultAsync(
        this._processParticularAttachments(measurement.attachments, attachmentNumber),
        approvedMeansDefault ?? true
      ))
    );
  }

  public async resetToDefaultAsync(descriptor: string, attachmentNumber?: number): Promise<void> {
    const measurement: IMeasurementViewModel | undefined = this._findMeasurement(descriptor);

    if (!measurement) return;
    const descriptorsToReset = this._processParticularAttachments(measurement.attachments, attachmentNumber);

    const valuesToUndo: ValuesCollection = await Promise.all(
      descriptorsToReset.map(async (descriptor) => {
        const value = await this._planningAlgorithm.getIValueAsync(descriptor);
        return [descriptor, value];
      })
    );

    await this._commandProcessor.executeAnonymousCommand(
      async (): Promise<undefined> => {
        await this._planningAlgorithm.resetTuneValuesAsync(descriptorsToReset);
      },
      async (): Promise<undefined> => {
        await this._planningAlgorithm.setTuneValuesAsync(valuesToUndo);
      }
    );

    this._findMeasurement(descriptor)?.setHighlighting(false);
    this._viVmController.locateMeasurementByDescriptor(descriptor);
  }

  private _processParticularAttachments(attachments: Array<string>, n: number | undefined): Array<string> {
    return n === undefined ? attachments : [attachments[n]];
  }
}
